import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AppContext } from "../../../../../appContext";
import { PopupWrapper } from "../../../../../components/styled/BasicBlocks";
import { SideMenuCommonHeader } from "../SideMenuCommon";
import "./SideMenuAdminUsers.css";
import { sendRequest, disableUser } from "../../../../../api/api";
import { getAvatarByKey, getAvatarList } from "../../../../../utils/avatars";

interface IUser {
    id: string;
    firstName?: string;
    lastName?: string;
    Disabled?: boolean;
    customAvatarUrl?: string;
}

interface ISession {
    id: string;
    date: string;
    status: string;
    Status?: string; // Backend returns this with capital S
    PredictionID?: string;
    ClientId?: string;
    title?: string;
    dateTo?: string;
    Result?: {
        output: any;
    };
}

interface IClientSession {
    id: string;
    date: string;
    status: string;
    Status?: string; // Backend returns this with capital S
    PredictionID?: string;
    ClientId?: string;
    title?: string;
    dateTo?: string;
    Result?: {
        output: any;
    };
}

export const SideMenuAdminUsers = ({ onClose }: { onClose: () => void }) => {
    const { translate } = useContext(AppContext);
    const [users, setUsers] = useState<IUser[]>([]);
    const [userSessions, setUserSessions] = useState<ISession[]>([]);
    const [userLogs, setUserLogs] = useState<{message: string, timestamp: string}[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [showJsonMap, setShowJsonMap] = useState<Record<string, boolean>>({});
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [showClientsModal, setShowClientsModal] = useState(false);
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [isLoadingClients, setIsLoadingClients] = useState(false);
    const [userClients, setUserClients] = useState<{id: string, Name: string, Soname: string}[]>([]);
    const [showClientSessionsModal, setShowClientSessionsModal] = useState(false);
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
    const [clientSessions, setClientSessions] = useState<IClientSession[]>([]);
    const [isLoadingClientSessions, setIsLoadingClientSessions] = useState(false);

    useEffect(() => {
        sendRequest('getAllUsers', 'GET', undefined, '')
            .then(res => res.json())
            .then(data => setUsers(data));
    }, []);

    const handleLogsClick = (userId: string) => {
        setSelectedUserId(userId);
        setIsLoadingLogs(true);
        setShowLogsModal(true);
        sendRequest(`getUserLogs/${userId}`, 'GET', undefined, '')
            .then(res => res.json())
            .then(data => {
                const sortedLogs = [...data].sort((a, b) => 
                    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                );
                setUserLogs(sortedLogs);
            })
            .catch(error => {
                console.error('Error fetching logs:', error);
                setUserLogs([]);
            })
            .finally(() => {
                setIsLoadingLogs(false);
            });
    };

    const handleSessionsClick = (userId: string) => {
        setSelectedUserId(userId);
        setIsLoadingSessions(true); // Set loading to true before fetching sessions
        sendRequest(`getUserSessions/${userId}`, 'GET', undefined, '')
            .then(res => res.json())
            .then(data => {
                if (data && data.length > 0) {
                    const sessions = data.flatMap((sessionWrapper: any) => {
                        const [sessionId, sessionData] = Object.entries(sessionWrapper)[0];
                        return [{
                            id: sessionId,
                            ...sessionData as any
                        }];
                    });
                    
                    setUserSessions(sessions);
                } else {
                    
                    setUserSessions([]);
                }
            })
            .catch(error => {
                console.error('Error fetching sessions:', error);
                setUserSessions([]);
            })
            .finally(() => {
                setIsLoadingSessions(false); // Set loading to false after fetching sessions, regardless of success or failure
            });
    };

    const handleCloseLogsModal = () => {
        setShowLogsModal(false);
        setSelectedUserId(null);
        setUserLogs([]);
    };

    const handleClientsClick = (userId: string) => {
        setSelectedUserId(userId);
        setIsLoadingClients(true);
        setShowClientsModal(true);
        sendRequest(`getUserClients/${userId}`, 'GET', undefined, '')
            .then(res => res.json())
            .then(data => {
                setUserClients(data);
            })
            .catch(error => {
                console.error('Error fetching clients:', error);
                setUserClients([]);
            })
            .finally(() => {
                setIsLoadingClients(false);
            });
    };

    const handleCloseClientsModal = () => {
        setShowClientsModal(false);
        setSelectedUserId(null);
        setUserClients([]);
    };

    const handleClientSessionClick = (clientId: string) => {
        setSelectedClientId(clientId);
        setIsLoadingClientSessions(true);
        setShowClientSessionsModal(true);

        sendRequest(`getClientSessions/${selectedUserId}/${clientId}`, 'GET', undefined, '')
            .then(res => res.json())
            .then(data => {
                if (data && data.length > 0) {
                    const sessions = data.flatMap((sessionWrapper: any) => {
                        const [sessionId, sessionData] = Object.entries(sessionWrapper)[0];
                        return [{
                            id: sessionId,
                            ...sessionData as any
                        }];
                    });
                    console.log('Processed client sessions:', sessions);
                    setClientSessions(sessions);
                } else {
                    console.warn('No sessions found for client:', clientId);
                    setClientSessions([]);
                }
            })
            .catch(error => {
                console.error('Error fetching client sessions:', error);
                setClientSessions([]);
            })
            .finally(() => {
                setIsLoadingClientSessions(false);
            });
    };

    const handleCloseClientSessionsModal = () => {
        setShowClientSessionsModal(false);
        setSelectedClientId(null);
        setClientSessions([]);
    };

    return <PopupWrapper className="SideMenuAdminUsers">
        <SideMenuCommonHeader text={translate('admin_users')} onClose={onClose} />
        <div className="SideMenuAdminUsers_list">
            {users.map(user => (
                <div key={user.id} className={`SideMenuAdminUsers_item ${user.Disabled ? 'SideMenuAdminUsers_item--disabled' : ''}`}>
                    <div className="SideMenuAdminUsers_info">
                        <div className="SideMenuAdminUsers_avatar">
                            <img 
                                src={user.customAvatarUrl || getAvatarByKey(getAvatarList()[user.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % getAvatarList().length])} 
                                alt={translate('alt_user_avatar')}
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = getAvatarByKey(getAvatarList()[user.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % getAvatarList().length]);
                                }}
                            />
                        </div>
                        <div className="SideMenuAdminUsers_text">
                            <div>{user.id}</div>
                            <div>{[user.firstName, user.lastName].filter(Boolean).join(' ') || '-'}</div>
                        </div>
                    </div>
                    <div className="SideMenuAdminUsers_buttons">
                        <button onClick={() => {
                            disableUser(user.id, !user.Disabled)
                                .then(response => {
                                    if (response.ok) {
                                        // Обновляем список пользователей
                                        sendRequest('getAllUsers', 'GET', undefined, '')
                                            .then(res => res.json())
                                            .then(data => setUsers(data));
                                    } else {
                                        response.text().then(text => alert(translate('common_error') + ': ' + text));
                                    }
                                })
                                .catch(error => alert(translate('common_error') + ': ' + error.message));
                        }}>{translate(user.Disabled ? 'admin_enable_user' : 'admin_disable_user')}</button>
                        <button onClick={() => handleSessionsClick(user.id)}>{translate('admin_all_sessions')}</button>
                        <button onClick={() => handleClientsClick(user.id)}>{translate('admin_clients')}</button>
                        <button onClick={() => handleLogsClick(user.id)}>{translate('admin_logs')}</button>
                    </div>
                </div>
            ))}
        </div>
        {selectedUserId && showLogsModal && (
            <>
                <div className="SideMenuAdminUsers_modal_overlay" onClick={handleCloseLogsModal}/>
                <div className="SideMenuAdminUsers_modal">
                    <button onClick={handleCloseLogsModal} className="SideMenuAdminUsers_modal_close">&times;</button>
                    <h4>{translate('admin_user_logs')}</h4>
                    <div className="SideMenuAdminUsers_logs_panel">
                        {isLoadingLogs ? (
                            <div>Loading logs...</div>
                        ) : (
                            userLogs.map((log, index) => (
                                <div key={index} className="SideMenuAdminUsers_log_item">
                                    <div className="SideMenuAdminUsers_log_time">
                                        {new Date(log.timestamp).toLocaleString()}
                                    </div>
                                    <div className="SideMenuAdminUsers_log_message">
                                        {log.message}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </>
        )}
        {selectedUserId && !showLogsModal && (
            <>
                <div className="SideMenuAdminUsers_modal_overlay" onClick={() => setSelectedUserId(null)}/>
                <div className="SideMenuAdminUsers_modal">
                    <button onClick={() => setSelectedUserId(null)} className="SideMenuAdminUsers_modal_close">&times;</button>
                    <h4>{translate('admin_user_sessions')}</h4>
                    <div className="SideMenuAdminUsers_sessions_panel">
                        {isLoadingSessions ? (
                            <div className="SideMenuAdminUsers_session">
                                {translate('loading_sessions')}
                            </div>
                        ) : userSessions && userSessions.length > 0 ? (
                            userSessions.map((session, index) => {
                                const showJsonKey = `showJson-${session.id}-${index}`;
                                const toggleJson = () => {
                                    setShowJsonMap(prev => ({...prev, [showJsonKey]: !prev[showJsonKey]}));
                                };
                                return (
                                    <div key={`${session.id}-${index}`} className="SideMenuAdminUsers_session">
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_session_id')}:</strong> {session.id}
                                        </div>
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_prediction_id')}:</strong> {session.PredictionID || '-'}
                                        </div>
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_date')}:</strong> {session.date ? new Date(session.date).toLocaleString() : '-'}
                                        </div>
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_client_id')}:</strong> {session.ClientId || '-'}
                                        </div>
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_title')}:</strong> {session.title || '-'}
                                        </div>
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_status')}:</strong> {session.Status || '-'}
                                        </div>
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_date_to')}:</strong> {session.dateTo ? new Date(session.dateTo).toLocaleString() : '-'}
                                        </div>
                                        <button
                                            className="SideMenuAdminUsers_json_button"
                                            onClick={toggleJson}
                                        >
                                            {showJsonMap[showJsonKey] ? translate('admin_hide_json') : translate('admin_show_json')}
                                        </button>
                                        {showJsonMap[showJsonKey] && createPortal(
                                            <>
                                                <div className="SideMenuAdminUsers_json_overlay" onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleJson();
                                                }}/>
                                                <div className="SideMenuAdminUsers_json_content">
                                                    <button className="SideMenuAdminUsers_json_close" onClick={toggleJson}>×</button>
                                                    <pre>
                                                        {session.Result ? 
                                                            JSON.stringify(session.Result, null, 2) : 
                                                            "No analysis result available for this session.\n\nThis could mean:\n• Session is still being processed\n• Analysis failed or was not completed\n• Session doesn't contain analyzable content\n\nSession Status: " + (session.Status || 'Unknown')
                                                        }
                                                    </pre>
                                                </div>
                                            </>, 
                                            document.body
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="SideMenuAdminUsers_session">
                                {translate('no_sessions')}
                            </div>
                        )}
                    </div>
                </div>
            </>
        )}
    {selectedUserId && showClientsModal && (
            <>
                <div className="SideMenuAdminUsers_modal_overlay" onClick={handleCloseClientsModal}/>
                <div className="SideMenuAdminUsers_modal">
                    <button onClick={handleCloseClientsModal} className="SideMenuAdminUsers_modal_close">&times;</button>
                    <h4>{translate('admin_user_clients')}</h4>
                    <div className="SideMenuAdminUsers_logs_panel">
                        {isLoadingClients ? (
                            <div className="SideMenuAdminUsers_loading">Loading clients...</div>
                        ) : userClients.length > 0 ? (
                            userClients.map((client) => (
                                <div key={client.id} className="SideMenuAdminUsers_client_item">
                                    <div className="SideMenuAdminUsers_log_item_info">
                                        <div><strong>ID:</strong> {client.id}</div>
                                        <div><strong>{translate('admin_client_name')}:</strong> {[client.Name, client.Soname].filter(Boolean).join(' ')}</div>
                                    </div>
                                    <button 
                                        className="SideMenuAdminUsers_client_session"
                                        onClick={() => handleClientSessionClick(client.id)}
                                    >
                                        Session
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="SideMenuAdminUsers_no_logs">No clients available</div>
                        )}
                    </div>
                </div>
            </>
        )}
        {selectedClientId && showClientSessionsModal && (
            <>
                <div className="SideMenuAdminUsers_client_sessions_modal_overlay" onClick={handleCloseClientSessionsModal}/>
                <div className="SideMenuAdminUsers_client_sessions_modal">
                    <button onClick={handleCloseClientSessionsModal} className="SideMenuAdminUsers_modal_close">&times;</button>
                    <h4>Client Sessions</h4>
                    <div className="SideMenuAdminUsers_sessions_panel">
                        {isLoadingClientSessions ? (
                            <div className="SideMenuAdminUsers_session">
                                Loading sessions...
                            </div>
                        ) : clientSessions && clientSessions.length > 0 ? (
                            clientSessions.map((session, index) => {
                                const showJsonKey = `showJson-${session.id}-${index}`;
                                const toggleJson = () => {
                                    setShowJsonMap(prev => ({...prev, [showJsonKey]: !prev[showJsonKey]}));
                                };
                                return (
                                    <div key={`${session.id}-${index}`} className="SideMenuAdminUsers_session">
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_session_id')}:</strong> {session.id}
                                        </div>
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_prediction_id')}:</strong> {session.PredictionID || '-'}
                                        </div>
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_date')}:</strong> {session.date ? new Date(session.date).toLocaleString() : '-'}
                                        </div>
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_client_id')}:</strong> {session.ClientId || '-'}
                                        </div>
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_title')}:</strong> {session.title || '-'}
                                        </div>
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_status')}:</strong> {session.Status || '-'}
                                        </div>
                                        <div className="SideMenuAdminUsers_session_row">
                                            <strong>{translate('admin_date_to')}:</strong> {session.dateTo ? new Date(session.dateTo).toLocaleString() : '-'}
                                        </div>
                                        <button
                                            className="SideMenuAdminUsers_json_button"
                                            onClick={toggleJson}
                                        >
                                            {showJsonMap[showJsonKey] ? translate('admin_hide_json') : translate('admin_show_json')}
                                        </button>
                                        {showJsonMap[showJsonKey] && createPortal(
                                            <>
                                                <div className="SideMenuAdminUsers_json_overlay" onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleJson();
                                                }}/>
                                                <div className="SideMenuAdminUsers_json_content">
                                                    <button className="SideMenuAdminUsers_json_close" onClick={toggleJson}>×</button>
                                                    <pre>
                                                        {session.Result ? 
                                                            JSON.stringify(session.Result, null, 2) : 
                                                            "No analysis result available for this session.\n\nThis could mean:\n• Session is still being processed\n• Analysis failed or was not completed\n• Session doesn't contain analyzable content\n\nSession Status: " + (session.Status || 'Unknown')
                                                        }
                                                    </pre>
                                                </div>
                                            </>, 
                                            document.body
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="SideMenuAdminUsers_session">
                                No sessions available for this client.
                            </div>
                        )}
                    </div>
                </div>
            </>
        )}
    </PopupWrapper>
}